"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CircleCheck, ArrowLeft, ChevronDown } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Introduction() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const faqItems = [{
    id: 1,
    question: "תוך כמה זמן ניתן להזמין מטפל סיעודי?",
    answer: "אנו מתחייבים לאספקת מטפל סיעודי מקצועי תוך עד 5 שעות בלבד מרגע הפנייה, בפריסה ארצית מלאה."
  }, {
    id: 2,
    question: "האם אתם מספקים מטפל סיעודי בבית או גם בבית חולים?",
    answer: "השירות שלנו גמיש לחלוטין. ניתן להזמין מטפל סיעודי בבית למגורים קבועים או זמניים, וכן השגחה פרטית בבתי חולים בכל רחבי הארץ."
  }, {
    id: 3,
    question: "מה היתרון של מטפל סיעודי פרטי ישראלי?",
    answer: "מטפל סיעודי פרטי ישראלי מבטיח תקשורת רציפה בשפה העברית, הבנה עמוקה של המנטליות המקומית ויכולת ליצור קשר רגשי חזק עם המטופל."
  }, {
    id: 4,
    question: "האם השירות של מטפל סיעודי זמין בשבתות וחגים?",
    answer: "כן, המוקד שלנו והמטפלים שלנו זמינים 24/7, כולל סופי שבוע וחגים, כדי להבטיח שקט נפשי למשפחה בכל עת."
  }, {
    id: 5,
    question: "איך מתבצע תהליך הבחירה של מטפל סיעודי בבית?",
    answer: "אנו מבצעים התאמה אישית המבוססת על הצרכים הרפואיים, המיקום הגיאוגרפי והעדפות האישיות של המטופל כדי להבטיח חיבור מוצלח."
  }, {
    id: 6,
    question: "האם יש התחייבות ארוכת טווח עבור מטפל סיעודי פרטי?",
    answer: "אין כל התחייבות ארוכת טווח. ניתן להזמין מטפל סיעודי פרטי למספר שעות בודדות, ימים או תקופות ארוכות לפי הצורך המשתנה."
  }];
  return <section id="הקדמה" className="relative bg-background overflow-hidden py-16 lg:py-24 min-h-[80vh] flex items-start">
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-chart-4/10 rounded-lg blur-[100px] -z-10 pointer-events-none translate-y-1/2 -translate-x-1/4" />

      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          <div ref={textRef} className={`flex flex-col items-end text-right transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground leading-tight mb-8">
              זמינות מיידית לטיפול מסור תוך <span className="text-primary">5 שעות</span>
            </h1>
            
            <div className="text-lg md:text-xl text-muted-foreground mb-12 max-w-2xl leading-relaxed">
              <p className="mb-6">
                כאשר המשפחה מתמודדת עם מצב בריאותי מורכב של אחד מיקיריה, הצורך במציאת <Link to="/CaregiverServicePagesPage" className="text-primary underline font-bold">מטפל סיעודי</Link> מקצועי (כגון <Link to="/CaregiverServicePagesPage" className="text-primary underline font-bold">מטפל סיעודי בתל אביב</Link>) הופך לדחוף וקריטי מאי פעם. אצלנו ב-Siudy, אנו מבינים כי הזמן הוא גורם מכריע, ולכן אנו מתחייבים לספק פתרון של <strong>מטפל סיעודי בבית</strong> תוך פחות מ-5 שעות מרגע הפנייה. בחירת <strong>מטפל סיעודי פרטי</strong> אינה רק עניין של לוגיסטיקה, אלא החלטה הרת גורל שמשפיעה ישירות על איכות החיים והביטחון האישי של המטופל ושל בני משפחתו העוטפים אותו.
              </p>
              <p className="mb-6">
                אנו מתמחים בחיבור מהיר ומדויק בין משפחות לבין <strong>מטפל סיעודי</strong> ישראלי, דובר השפה, שמכיר את המנטליות המקומית ומסוגל להעניק לא רק טיפול פיזי מתקדם אלא גם תמיכה רגשית משמעותית ברגעים הקשים. השירות הייחודי שלנו כולל ליווי צמוד והתאמה אישית של <Link to="/CaregiverServicePagesPage2" className="text-primary underline font-bold">מטפל סיעודי בבית</Link> לצרכים הספציפיים של הקשיש או המטופל המשתקם. אנו מציעים פתרונות זמינים כמו <Link to="/CaregiverServicePagesPage2" className="text-primary underline font-bold">מטפל סיעודי בתל אביב</Link> או <Link to="/CaregiverServicePagesPage2" className="text-primary underline font-bold">מטפל סיעודי בחיפה</Link> להתאמה מושלמת. בין אם מדובר בהשגחה צמודה לאחר ניתוח מורכב או בסיוע יומיומי מתמשך, <strong>מטפל סיעודי פרטי</strong> מטעמנו יגיע אליכם במהירות שיא ובזמינות מלאה. למידע נוסף, אתם מוזמנים לקרוא <Link to="/CaregiverServicePagesPage2" className="text-primary underline font-bold">מאמר על טיפול סיעודי</Link>.
              </p>
              <p className="mb-6">
                המטרה העליונה שלנו היא להסיר את נטל הדאגה מהכתפיים שלכם ולדעת שיש <strong>מטפל סיעודי</strong> מיומן שנמצא שם בכל רגע נתון. אנו מעניקים שירות רחב הכולל גם <Link to="/CaregiverServicePagesPage" className="text-primary underline font-bold">מטפל סיעודי בחיפה</Link>, שירותי <Link to="/CaregiverServicePagesPage" className="text-primary underline font-bold">השגחה בבית חולים</Link> ובכל רחבי הארץ. אנו מאמינים שכל אדם ראוי להזדקן בכבוד בסביבתו הטבעית והחמה, ולכן שירותי <Link to="/CaregiverServicePagesPage2" className="text-primary underline font-bold">מטפל סיעודי בבית</Link> שלנו מתוכננים להעניק את המעטפת המקצועית והאנושית ביותר שניתן לבקש. עם <strong>מטפל סיעודי פרטי</strong> המנוסה בטיפול במצבים רפואיים מורכבים, אתם יכולים להיות רגועים לחלוטין שהיקר לכם מכל נמצא בידיים הבטוחות, המיומנות והמסורות ביותר בישראל.
              </p>
              <p className="text-base font-medium">
                מחפשים שירות גם באזור אחר? אנו מספקים מענה מהיר גם ב<Link to="/CaregiverServicePagesPage2" className="text-primary underline font-bold">רמת גן</Link> ו<Link to="/CaregiverServicePagesPage2" className="text-primary underline font-bold">אשדוד</Link>.
              </p>
            </div>

            <div className="mb-12 text-right w-full">
              <h2 className="text-3xl font-bold text-foreground mb-6">הפתרון שלנו: סיעוד מיידי ללא פשרות</h2>
              <div className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                <p className="mb-4">
                  הפתרון שאנו מציעים ב-Siudy מבוסס על מודל חדשני של "סיעוד מיידי". במקום להמתין ימים ארוכים לאישור חברות כוח אדם או לעבור תהליכי בירוקרטיה מתישים מול מוסדות, אנו מפעילים מערך זמין של מטפלים ישראלים הפרוסים בכל רחבי הארץ. הפתרון שלנו נותן מענה ישיר למחסור החמור במטפלים זמינים, במיוחד במצבי חירום שבהם המטופל משתחרר בפתאומיות מבית החולים או חווה התדרדרות פתאומית במצבו הבריאותי.
                </p>
                <p>
                  אנו מבטיחים הגעה של מטפל מקצועי לביתכם תוך 5 שעות בלבד, מה שמאפשר רצף טיפולי קריטי ללא פשרות. המטפלים שלנו עוברים סינון קפדני ביותר ומתמחים במגוון רחב של שירותים סיעודיים, החל מעזרה ברחצה והלבשה ועד לניהול תרופות מורכב וליווי לביקורים רפואיים. זהו פתרון מקיף שנועד לתת שקט נפשי אמיתי למשפחה ולשפר את איכות החיים של המטופל בצורה מיידית ומורגשת.
                </p>
              </div>
            </div>

            <div className="mb-12 text-right w-full">
              <h2 className="text-3xl font-bold text-foreground mb-6">היתרונות המרכזיים בשירות שלנו</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl ml-auto">
                {["זמינות שיא: מטפל בבית תוך 5 שעות בלבד מרגע הקריאה.", "צוות כחול-לבן: מטפלים ישראלים דוברי עברית לתקשורת מיטבית.", "התאמה אישית: בחירת המטפל המתאים ביותר לאופיו ולצרכיו של המטופל.", "גמישות מלאה: שירות לפי שעות, לילות או סופי שבוע בהתאם לצורך.", "בטחון ושקט נפשי: ידיעה שיש מי שמשגיח ומטפל במקצועיות ללא פשרות.", "ליווי מקצועי: מענה אנושי וזמין לכל שאלה או שינוי בצרכי הטיפול."].map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-3 justify-end bg-card/50 p-4 rounded-xl border border-border">
                    <span className="text-foreground font-medium">{benefit}</span>
                    <CircleCheck className="text-chart-2 size-5 shrink-0 mt-1" />
                  </div>)}
              </div>
            </div>

            <div className="mb-12 text-right w-full">
              <h2 className="text-3xl font-bold text-foreground mb-6">מידע מפורט על שירותי הטיפול</h2>
              <div className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                <p className="mb-4">
                  הטיפול הסיעודי המודרני דורש הרבה מעבר לעזרה טכנית פשוטה. הוא דורש אמפתיה עמוקה, סבלנות אינסופית וידע מקצועי נרחב. המטפלים שלנו מיומנים בטיפול בחולי דמנציה, אלצהיימר, ומשתקמים לאחר אירועים מוחיים או ניתוחים אורתופדיים מורכבים. אנו שמים דגש רב על שימור היכולות הקיימות של המטופל ועידוד עצמאות ככל הניתן בסביבתו הטבעית.
                </p>
                <p className="mb-4">
                  בנוסף לטיפול הפיזי השוטף, המטפל מהווה חברה אנושית ומשענת רגשית משמעותית, מה שמפחית משמעותית תחושות של בדידות ודיכאון שלעיתים קרובות מלוות את הגיל השלישי. אנו עובדים בשקיפות מלאה מול בני המשפחה ומעדכנים בכל שינוי קטן במצב המטופל.
                </p>
                <p>
                  השירות שלנו ניתן בבית המטופל, בבתי אבות או בבתי חולים כהשגחה פרטית, ומספק מעטפת הגנה של 360 מעלות. בחירה ב-Siudy היא בחירה בסטנדרט הגבוה ביותר של טיפול סיעודי בישראל, עם דגש על מהירות, אנושיות ומקצועיות ללא פשרות.
                </p>
              </div>
            </div>

            <div className="mb-12 text-right w-full max-w-2xl">
              <h2 className="text-3xl font-bold text-foreground mb-8">שאלות נפוצות</h2>
              <div className="space-y-4">
                {faqItems.map((item, i) => <details key={item.id} data-index={i} className="group border border-border rounded-2xl bg-card/30 backdrop-blur-sm overflow-hidden transition-all duration-300 open:bg-card/80">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none font-semibold text-foreground text-lg hover:text-primary transition-colors">
                      <ChevronDown className="size-5 text-muted-foreground transition-transform duration-300 group-open:rotate-180" />
                      <span>{item.question}</span>
                    </summary>
                    <div className="p-5 pt-0 text-muted-foreground text-base leading-relaxed border-t border-border/50 mt-2">
                      {item.answer}
                    </div>
                  </details>)}
              </div>
            </div>

            <div className="bg-card/80 backdrop-blur-md border border-border rounded-2xl p-4 pr-6 pl-8 inline-flex items-center gap-4 mb-10 shadow-sm">
              <span className="font-semibold text-foreground text-lg font-default">
                צוות מטפלים ישראלים בלבד
              </span>
              <CircleCheck className="text-chart-2 size-7 shrink-0" />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-end mb-12">
              <Button size="lg" className="text-lg px-8 h-14 rounded-xl shadow-lg shadow-primary/20" asChild>
                <Link to="#">
                  צור קשר עכשיו
                  <ArrowLeft className="ml-2 size-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 h-14 rounded-xl" asChild>
                <Link to="#">
                  לכל השירותים שלנו
                </Link>
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto justify-end border-t border-border pt-12">
              <Button size="lg" variant="secondary" className="text-lg px-8 h-14 rounded-xl" asChild>
                <Link to="#">
                  התייעצות טלפונית חינם
                </Link>
              </Button>
              <Button size="lg" variant="ghost" className="text-lg px-8 h-14 rounded-xl border border-dashed border-border" asChild>
                <Link to="#">
                  למדריך המלא לסיעוד
                </Link>
              </Button>
            </div>
          </div>

          <div ref={imageRef} className={`relative lg:sticky lg:top-24 transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-shadow-color/20 border border-border/50">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/israeli_caregiver_elderly_home.png" alt="מטפל ישראלי מעניק טיפול מסור לקשיש בסביבה ביתית חמה" className="w-full h-auto object-cover aspect-[4/3] lg:aspect-[4/5]" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent pointer-events-none" />
            </div>
            
            <div className="absolute -bottom-6 -right-6 bg-card border border-border p-6 rounded-2xl shadow-xl hidden md:block">
              <div className="flex flex-col items-end">
                <span className="text-3xl font-bold text-primary">5 שעות</span>
                <span className="text-sm text-muted-foreground">זמן הגעה ממוצע</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}